import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MessageSquare,
  Usb,
  ServerCog,
  Ban,
  CircleDollarSign,
  Ghost,
  Apple,
  Smartphone,
  ShieldCheck,
  EyeOff,
  KeyRound,
  PhoneOff,
} from "lucide-react";
import PhantomChatWaitlistModal from "../components/PhantomChatWaitlistModal";

const Chat = () => {
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Phantom X · Phantom Industries";
  }, []);

  return (
    <>
      <main className="bg-black text-white" data-testid="chat-page">
        {/* Hero */}
        <section
          className="relative pt-32 pb-24 md:pb-32 border-b border-white/5 overflow-hidden"
          data-testid="phantom-chat-section"
        >
          <div className="absolute inset-0 aurora-purple opacity-60 pointer-events-none" />
          <div className="absolute inset-0 grid-mask pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-14 items-center">
              <div>
                <div className="chip">
                  <MessageSquare className="h-3 w-3" /> Phantom X · iOS + Android · Coming soon
                </div>
                <h1
                  className="mt-5 font-headline text-5xl md:text-7xl font-bold text-gradient-purple leading-[0.95]"
                  data-testid="phantom-chat-heading"
                >
                  An account you<br />can hold in your hand.
                </h1>
                <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-lg">
                  Phantom X is an encrypted messenger with{" "}
                  <span className="text-white/90">no phone number, no email, no identity</span>.
                  Every other encrypted messenger stores an account somewhere.{" "}
                  <span className="text-purple-300">Ours only exists while a physical Phantom Key is plugged in.</span>
                </p>

                <div className="mt-10 flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setWaitlistOpen(true)}
                    className="btn-primary"
                    data-testid="phantom-chat-notify-btn"
                  >
                    Join the waitlist <ArrowRight className="h-4 w-4" />
                  </button>
                  <a
                    href="https://t.me/Phantom0091"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ghost"
                    data-testid="phantom-chat-telegram-btn"
                  >
                    Or DM us on Telegram
                  </a>
                </div>
                <div className="mt-6 flex items-center gap-2 text-xs text-white/50">
                  <Apple className="h-4 w-4" /> iOS
                  <span className="text-white/20 mx-1">·</span>
                  <Smartphone className="h-4 w-4" /> Android
                  <span className="text-white/20 mx-1">·</span>
                  <Ghost className="h-4 w-4" /> Anonymous
                </div>
              </div>

              {/* Phantom X hero */}
              <div className="relative">
                <div
                  className="relative rounded-3xl overflow-hidden card-glass aspect-square"
                  data-testid="phantom-key-hero"
                >
                  {/* Purple aurora + grain backdrop behind transparent logo */}
                  <div className="absolute inset-0 aurora-purple opacity-80 pointer-events-none" />
                  <div className="absolute inset-0 grid-mask opacity-40 pointer-events-none" />
                  <img
                    src="/img/phantom-chat-hero-logo.png"
                    alt="Phantom X — encrypted messenger app icon"
                    className="absolute inset-0 w-full h-full object-contain p-10 drop-shadow-[0_0_60px_rgba(168,85,247,0.55)]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                  <div className="absolute top-6 left-6 chip">
                    <MessageSquare className="h-3 w-3" /> Phantom X · Encrypted
                  </div>
                  <div className="absolute bottom-20 left-8 right-8 md:bottom-24">
                    <div className="text-xs uppercase tracking-[0.2em] text-purple-300 mb-2">
                      Hardware-keyed · Zero-account
                    </div>
                    <div className="font-headline text-3xl md:text-4xl font-bold text-white leading-[1.05]">
                      Not a login.<br />A key you can hold.
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -left-2 md:-left-6 card-glass rounded-2xl px-5 py-3 border border-purple-500/40 max-w-[260px]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300">
                    Zero-account
                  </div>
                  <div className="font-display text-sm md:text-base font-bold text-white mt-0.5">
                    No phone. No email. No trail.
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 md:-right-6 card-glass rounded-2xl px-5 py-3 border border-red-500/40 hidden md:block">
                  <div className="flex items-center gap-2">
                    <PhoneOff className="h-4 w-4 text-red-300" />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-red-300">
                        No key
                      </div>
                      <div className="font-display text-sm font-bold text-white">
                        No app.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works — 4 steps */}
        <section className="relative py-24 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="chip"><ServerCog className="h-3 w-3" /> How it works</div>
            <h2 className="mt-4 font-headline text-4xl md:text-5xl font-bold text-white">
              Four steps. Then radio silence.
            </h2>

            <div className="mt-12 grid md:grid-cols-4 gap-4">
              <Step n="1" icon={CircleDollarSign} title="Purchase the app"
                body="One-time payment from our site, the App Store or Play Store. Anonymous — no phone, no email, no ID required." />
              <Step n="2" icon={Usb} title="Load your .phantom file"
                body="We give you a signed .phantom credential file. You copy it once to any USB-C Phantom Key. That's your identity — physical, holdable, transferable." />
              <Step n="3" icon={ServerCog} title="Plug in. Sign in."
                body="The app checks the key on every launch. Without it inserted, the app cannot authenticate, cannot reach our servers, and shows nothing." />
              <Step n="4" icon={Ban} title="Pull the key = you're gone."
                body="Unplug and the session ends. No password, no phone number, no email — nothing on the app to hack. The door is the key. The key is in your pocket." />
            </div>
          </div>
        </section>

        {/* Product screens — 3-phone showcase */}
        <section
          className="relative py-24 border-b border-white/5 overflow-hidden"
          data-testid="phantom-chat-screens"
        >
          <div className="absolute inset-0 aurora-purple opacity-30 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <div className="chip">
                <MessageSquare className="h-3 w-3" /> Inside the app
              </div>
              <h2 className="mt-4 font-headline text-4xl md:text-5xl font-bold text-white">
                Familiar to use.<br />Impossible to trace.
              </h2>
              <p className="mt-4 text-white/60 max-w-xl">
                Everything you'd expect from a modern messenger — group chats,
                voice + video calls, disappearing messages, file share — with a
                security model nothing else on the App Store can match.
              </p>
            </div>

            <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-10 max-w-4xl mx-auto">
              {[
                {
                  key: "empty",
                  label: "No metadata. No mystery.",
                  headline: "Zero contacts until you invite them.",
                  body: "Empty inbox on first launch — no address book access, no cross-app matching. You add friends by scanning their Phantom ID QR in person, or pasting one they sent you.",
                  src: "/img/phantom-x-screen-empty.jpg",
                },
                {
                  key: "chat",
                  label: "Encrypted conversation",
                  headline: "Text, voice, video, files.",
                  body: "Anything you'd send in any messenger — including PDFs, images and voice notes — sealed with per-conversation keys we never see or store.",
                  src: "/img/phantom-chat-screen-chat.jpg",
                },
              ].map((s) => (
                <div key={s.key} className="flex flex-col items-center text-center">
                  <div className="relative w-full max-w-[280px]">
                    {/* Phone frame */}
                    <div className="relative rounded-[2.5rem] border-[6px] border-white/10 bg-black/80 shadow-[0_20px_80px_-10px_rgba(168,85,247,0.35)] overflow-hidden aspect-[9/19]">
                      <img
                        src={s.src}
                        alt={`Phantom X — ${s.label}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                        data-testid={`chat-screen-${s.key}`}
                      />
                      {/* Notch */}
                      <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 rounded-full bg-black/90 border border-white/10" />
                    </div>
                  </div>
                  <div className="mt-6 text-[11px] uppercase tracking-[0.25em] text-purple-300">
                    {s.label}
                  </div>
                  <div className="mt-2 font-display text-lg font-semibold text-white leading-snug max-w-xs">
                    {s.headline}
                  </div>
                  <p className="mt-2 text-sm text-white/55 leading-relaxed max-w-xs">
                    {s.body}
                  </p>
                </div>
              ))}
            </div>

            {/* App-icon + badge row */}
            <div className="mt-16 rounded-3xl card-glass border border-white/10 p-8 flex flex-col md:flex-row items-center gap-6">
              <img
                src="/img/phantom-chat-icon.png"
                alt="Phantom X app icon"
                className="h-24 w-24 rounded-2xl drop-shadow-[0_0_40px_rgba(168,85,247,0.45)] flex-shrink-0"
              />
              <div className="flex-1 text-center md:text-left">
                <div className="text-xs uppercase tracking-[0.25em] text-purple-300">
                  App icon · Phantom X
                </div>
                <div className="mt-2 font-display text-xl font-bold text-white">
                  Coming to the App Store and Google Play.
                </div>
                <div className="mt-1 text-sm text-white/55">
                  Join the waitlist and we'll drop a launch-day download link
                  the moment we're live.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setWaitlistOpen(true)}
                className="btn-primary flex-shrink-0"
                data-testid="chat-screens-notify-btn"
              >
                Notify me on launch <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </section>

        {/* Why it matters */}
        <section className="relative py-24 border-b border-white/5">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-6">
              <Pillar icon={EyeOff} title="No trail to follow" body="No account = no metadata trail. There is nothing linking the app to you until you plug in — and nothing left behind when you pull out." />
              <Pillar icon={KeyRound} title="Your key. Your control." body="Lost or stolen? The Phantom Key is fingerprint-locked. Even we can't unlock it. You revoke the key on your next login from a spare." />
              <Pillar icon={ShieldCheck} title="Our servers. Our rules." body="Phantom Industries hosts the infrastructure — not AWS, not Google, not Meta. Jurisdictions we chose. Data we minimise. Encryption we don't hold the keys to." />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="chip">FAQ</div>
            <h2 className="mt-4 font-headline text-4xl md:text-5xl font-bold text-white">
              The questions we get most.
            </h2>
            <div className="mt-10 space-y-3">
              <Faq q="Do I need a Phantom Phone to use Phantom X?"
                a="No. Phantom X runs on any modern iOS or Android device with a USB-C port. A Phantom Phone is the safest hardware to pair with it, but not required." />
              <Faq q="What if I lose my Phantom Key?"
                a="Log in from a spare Phantom Key with your own signed .phantom file, hit Revoke on the lost key, and issue a fresh one. Because the key is fingerprint-locked, whoever finds it can't log in as you." />
              <Faq q="What makes Phantom X different from every other encrypted messenger?"
                a="Everyone else stores something. A phone number, an email, an account row in a database. Anything sitting on a server can be seized, subpoenaed, hacked or leaked. Phantom X's account literally does not exist while your key is unplugged — there is nothing on our servers to seize. We're what our competitors are scared of." />
              <Faq q="Where are your servers?"
                a="We own and operate our own bare-metal servers in jurisdictions selected for privacy law. No AWS, no Google Cloud, no third-party cloud provider ever sees your traffic." />
              <Faq q="How much will it cost?"
                a="One-time purchase — no subscription. Final pricing announced at launch. Waitlist gets an early-adopter discount." />
              <Faq q="Can I buy the Phantom Key separately?"
                a="Yes. On launch we'll ship the Phantom Key USB-C token as its own SKU. Bring your own key from an earlier order and it's compatible." />
            </div>
          </div>
        </section>

        {/* Back to Phantom Industries */}
        <section className="py-16 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Also from Phantom Industries</div>
            <div className="mt-4">
              <Link to="/phones" className="btn-ghost">
                Phantom Phone — the hardened Pixel <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PhantomChatWaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </>
  );
};

const Step = ({ n, icon: Icon, title, body }) => (
  <div className="card-glass rounded-2xl p-6 h-full">
    <div className="flex items-center gap-3">
      <div className="h-8 w-8 rounded-lg bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
        <Icon className="h-4 w-4 text-purple-300" />
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/40">Step {n}</div>
    </div>
    <div className="mt-4 font-display font-semibold text-white">{title}</div>
    <div className="mt-2 text-sm text-white/60 leading-relaxed">{body}</div>
  </div>
);

const Pillar = ({ icon: Icon, title, body }) => (
  <div className="card-glass rounded-2xl p-6">
    <div className="h-10 w-10 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
      <Icon className="h-5 w-5 text-purple-300" />
    </div>
    <div className="mt-5 font-display text-lg font-semibold text-white">{title}</div>
    <div className="mt-2 text-sm text-white/60 leading-relaxed">{body}</div>
  </div>
);

const Faq = ({ q, a }) => (
  <details className="group card-glass rounded-2xl p-5 open:pb-5">
    <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
      <span className="font-display text-white font-medium">{q}</span>
      <ArrowRight className="h-4 w-4 text-purple-300 transition-transform group-open:rotate-90 flex-shrink-0" />
    </summary>
    <div className="mt-3 text-sm text-white/65 leading-relaxed">{a}</div>
  </details>
);

export default Chat;
